import clsx from "clsx";
import Modal from "../Modal/Modal";
import TextInput from "../TextInput/TextInput";
import CustomSelect, { ECustomSelectIconType } from "../Select/CustomSelect";
import { useMemo, useState } from "react";
import RadioGroup from "../RadioGroup/RadioGroup";
import {
  ASK_CONFIRMATION_ON_SAVE_OR_UPDATE,
  ETransactionType,
} from "src/helpers/constants";
import { ERecurringTransactionFrequency } from "../RecurringTransaction/RecurringTransactionSettings";
import { useTranslation } from "react-i18next";
import { MdAdd, MdCheck, MdClose, MdSettings } from "react-icons/md";
import useModalsStore from "src/store/modalsStore";
import useAllTransactions from "src/hooks/useAllTransactions";
import {
  createRecurringTransaction,
  updateRecurringTransaction,
} from "src/api/recurring-transactions";
import { v4 as uuidv4 } from "uuid";
import useFrequentTransactions from "src/hooks/useFrequentTransactions";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { objectHasChanges, showNotification } from "src/helpers/utils";
import useCurrencies from "src/hooks/useCurrencies";
import useCurrentUser from "src/hooks/useCurrentUser";
import { map, uniq } from "lodash";
import NoTableData from "../NoTableData/NoTableData";
import { ROUTES } from "src/helpers/routes";
import { useNavigate } from "react-router-dom";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

export type TRecurringTransactionModalType = "create" | "edit";

export interface IRecurringTransactionModalPayload {
  mode?: TRecurringTransactionModalType;
  transactionId?: string;
  initialValues?: {
    nextPayment: string;
    total: number;
    type: ETransactionType;
    description: string;
    category: string;
    payTo: string;
    frequency: ERecurringTransactionFrequency;
    currency: string;
  };
}

const FrequentTransactionModal: React.FC<IRecurringTransactionModalPayload> = ({
  transactionId,
  initialValues,
  mode,
}) => {
  const navigate = useNavigate();
  const { allUserAccounts, hasRegisteredAccounts } = useCurrentUser();
  const [showCreateConfirmDialog, setShowCreateConfirmDialog] =
    useState<boolean>(false);
  const [currency, setCurrency] = useState<string>(
    initialValues?.currency || "",
  );

  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const { resetCache } = useFrequentTransactions();
  const {
    setShowCreateRecurringTransactionModal,
    setShowCreateNewAccountModal,
    setShowEditRecurringTransactionModal,
  } = useModalsStore();
  const { t } = useTranslation();
  const { allCategoriesObject, allSourcesObject, allDescriptionsObject } =
    useAllTransactions();
  const { getCurrencyDataByCode } = useCurrencies();
  const isEditMode = useMemo(() => mode === "edit", [mode]);

  const [date, setDate] = useState<string>(initialValues?.nextPayment || "");
  const [total, setTotal] = useState<string>(
    initialValues?.total ? `${initialValues?.total}` : "",
  );
  const [type, setType] = useState<ETransactionType>(
    initialValues?.type || ETransactionType.EXPENSE,
  );
  const [description, setDescription] = useState<string>(
    initialValues?.description || "",
  );
  const [clientProvider, setClientProvider] = useState<string>(
    initialValues?.payTo || "",
  );
  const [category, setCategory] = useState<string>(
    initialValues?.category || "",
  );
  const [selectedFrequency, setSelectedFrequency] =
    useState<ERecurringTransactionFrequency>(
      initialValues?.frequency || ERecurringTransactionFrequency.MONTHLY,
    );

  const frequencyOptions = useMemo(
    () => [
      // {
      //   value: ERecurringTransactionFrequency.FIXED,
      //   label: "Fecha fija",
      // },
      {
        value: ERecurringTransactionFrequency.WEEKLY,
        label: t("modals:recurrence_settings.frequency.options.weekly"),
      },
      {
        value: ERecurringTransactionFrequency.FORTNIGHT,
        label: t("modals:recurrence_settings.frequency.options.biweekly"),
      },
      {
        value: ERecurringTransactionFrequency.MONTHLY,
        label: t("modals:recurrence_settings.frequency.options.monthly"),
      },
      {
        value: ERecurringTransactionFrequency.QUARTER,
        label: t("modals:recurrence_settings.frequency.options.quarterly"),
      },
      {
        value: ERecurringTransactionFrequency.BIANNUAL,
        label: t("modals:recurrence_settings.frequency.options.semiannually"),
      },
      {
        value: ERecurringTransactionFrequency.ANNUAL,
        label: t("modals:recurrence_settings.frequency.options.annually"),
      },
    ],
    [t],
  );

  const closeModal = () => {
    if (!isRegistering) {
      if (isEditMode) {
        setShowEditRecurringTransactionModal(false, null);
      } else {
        setShowCreateRecurringTransactionModal(false);
      }
    }
  };

  const canSave = useMemo(() => {
    return (
      description &&
      category &&
      clientProvider &&
      date &&
      selectedFrequency &&
      currency &&
      total !== ""
    );
  }, [
    description,
    category,
    clientProvider,
    date,
    selectedFrequency,
    currency,
    total,
  ]);

  const registerTransaction = async () => {
    setIsRegistering(true);
    return await createRecurringTransaction({
      fieldsToSave: {
        transaction: uuidv4(),
        category,
        description,
        discount: 0,
        payTo: clientProvider,
        taxes: 0,
        subTotal: Number(total),
        total: Number(total),
        type,
        frequency: selectedFrequency,
        nextPayment: date,
        currency,
      },
    })
      .then(async () => {
        showNotification({
          kind: "success",
          message: "Transacción recurrente registrada",
        });
        await resetCache();
        setShowCreateRecurringTransactionModal(false);
      })
      .finally(() => {
        setIsRegistering(false);
      })
      .catch((e: any) => {
        console.log("Error", e);
      });
  };

  const updateTransaction = async () => {
    setIsUpdating(true);
    return await updateRecurringTransaction({
      fieldsToSave: {
        transaction: transactionId || "",
        category,
        description,
        discount: 0,
        payTo: clientProvider,
        taxes: 0,
        subTotal: Number(total),
        total: Number(total),
        type,
        frequency: selectedFrequency,
        nextPayment: date,
        currency,
      },
    })
      .then(async () => {
        showNotification({
          kind: "success",
          message: "Transacción recurrente actualizada",
        });
        await resetCache();
        setShowEditRecurringTransactionModal(false, null);
      })
      .finally(() => {
        setIsUpdating(false);
      })
      .catch((e: any) => {
        console.log("Error", e);
      });
  };

  const availableCurrencies = useMemo(() => {
    const uniqueCurrencies = uniq(map(allUserAccounts, "currency"));

    if (uniqueCurrencies.length) {
      return uniqueCurrencies.map((currency: any) => {
        return {
          value: currency,
          label: getCurrencyDataByCode({ code: currency }).name,
          iconType: ECustomSelectIconType.CURRENCY,
          icon: currency,
        };
      });
    }

    return [];
  }, [allUserAccounts]);

  const hasChanges = useMemo(() => {
    if (isEditMode) {
      return objectHasChanges({
        original: {
          nextPayment: initialValues?.nextPayment,
          type: initialValues?.type,
          description: initialValues?.description,
          category: initialValues?.category,
          payTo: initialValues?.payTo,
          frequency: initialValues?.frequency,
          total: initialValues?.total,
          currency: initialValues?.currency,
        },
        edited: {
          nextPayment: date,
          type,
          description,
          category,
          payTo: clientProvider,
          frequency: selectedFrequency,
          total,
          currency,
        },
      });
    }

    return false;
  }, [
    initialValues,
    date,
    type,
    description,
    category,
    clientProvider,
    selectedFrequency,
    total,
    currency,
  ]);

  const saveOrUpdateButtonIsDisabled = useMemo(() => {
    if (!canSave) {
      return true;
    }

    if (isEditMode) {
      return !hasChanges;
    }

    return false;
  }, [hasChanges, isEditMode, canSave]);

  return (
    <>
      <Modal
        onEscape={() => closeModal()}
        className={clsx(["!w-full", "md:!max-w-[600px]"])}
      >
        <Modal.Header
          onClose={() => closeModal()}
          title={
            isEditMode
              ? "Editar transacción recurrente"
              : "Nueva transacción recurrente"
          }
        />
        <Modal.Body>
          <div
            className={clsx(["flex", "flex-col", "gap-4", "w-full", "pb-2"])}
          >
            {hasRegisteredAccounts ? (
              <>
                <RadioGroup
                  columns={2}
                  options={[
                    {
                      value: ETransactionType.INCOME,
                      label: "Ingreso",
                      description: "Registrar como un ingreso recurrente",
                    },
                    {
                      value: ETransactionType.EXPENSE,
                      label: "Gasto",
                      description: "Registrar como un gasto recurrente",
                    },
                  ]}
                  selected={type}
                  onChange={(value) => {
                    setType(value as ETransactionType);
                  }}
                />
                <CustomSelect
                  onChange={(e: any) => setDescription(e?.value)}
                  value={description}
                  label={"Descripción"}
                  placeholder={"Selecciona una descripción o agrega una nueva"}
                  options={allDescriptionsObject}
                  creatable
                  allowSearchModal
                />
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-2",
                    "gap-4",
                    "w-full",
                  ])}
                >
                  <CustomSelect
                    onChange={(e: any) => setCategory(e?.value)}
                    value={category}
                    placeholder={t(
                      "pages:transaction.collapsibles.header.fields.category_placeholder",
                    )}
                    label={t(
                      "pages:transaction.collapsibles.header.fields.category",
                    )}
                    required
                    options={allCategoriesObject}
                    withIcons
                    creatable
                    allowSearchModal
                  />
                  <CustomSelect
                    creatable
                    required
                    value={clientProvider}
                    onChange={(e: any) => setClientProvider(e?.value)}
                    options={allSourcesObject}
                    withIcons
                    placeholder={"Selecciona un proveedor o agrega uno nuevo"}
                    label={"Proveedor"}
                    allowSearchModal
                  />
                </div>

                <div
                  className={clsx([
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-2",
                    "gap-4",
                    "w-full",
                  ])}
                >
                  <TextInput
                    type="date"
                    onChange={(e: any) => setDate(e?.target?.value)}
                    value={date}
                    label="Fecha de la próxima transacción"
                  />
                  <CustomSelect
                    options={frequencyOptions}
                    label={t("modals:recurrence_settings.frequency.label")}
                    placeholder={t(
                      "modals:recurrence_settings.frequency.placeholder",
                    )}
                    required
                    onChange={(e) => {
                      setSelectedFrequency(e?.value);
                    }}
                    value={selectedFrequency}
                    useValueAsLabel={false}
                    isSearchable={false}
                  />
                </div>

                <div
                  className={clsx([
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-2",
                    "gap-4",
                    "w-full",
                  ])}
                >
                  <TextInput
                    type="number"
                    onChange={(e: any) => setTotal(e?.target?.value)}
                    value={total}
                    label="Total"
                  />
                  <CustomSelect
                    required
                    label={t("pages:account.fields.currency")}
                    placeholder={t("pages:account.fields.currency_placeholder")}
                    options={availableCurrencies}
                    value={currency}
                    onChange={(e: any) => setCurrency(e?.value)}
                    useValueAsLabel={false}
                    allowSearchModal
                    withIcons
                  />
                </div>
              </>
            ) : (
              <NoTableData
                text="No tienes cuentas registradas"
                buttons={[
                  {
                    icon: <MdAdd />,
                    tooltip: t("common:buttons.add"),
                    onClick: () => setShowCreateNewAccountModal(true),
                  },
                  {
                    icon: <MdSettings />,
                    tooltip: t("misc:menu.settings"),
                    onClick: () => {
                      if (isEditMode) {
                        setShowEditRecurringTransactionModal(false, null);
                      } else {
                        setShowCreateRecurringTransactionModal(false);
                      }
                      navigate(ROUTES.SETTINGS_ACCOUNTS);
                    },
                  },
                ]}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer
          actions={[
            {
              text: isEditMode
                ? t("common:buttons.update")
                : t("common:buttons.save"),
              onClick: () =>
                ASK_CONFIRMATION_ON_SAVE_OR_UPDATE
                  ? setShowCreateConfirmDialog(true)
                  : isEditMode
                    ? updateTransaction()
                    : registerTransaction(),
              kind: EButtonKind.SAVE,
              icon: <MdCheck size={ICON_SIZES.SM} />,
              disabled: saveOrUpdateButtonIsDisabled || isRegistering,
              isLoading: isRegistering,
              hide: !hasRegisteredAccounts,
            },
            {
              text: t("common:buttons.close"),
              onClick: () => closeModal(),
              kind: EButtonKind.NORMAL,
              icon: <MdClose size={ICON_SIZES.SM} />,
              disabled: isRegistering,
            },
          ]}
        />
      </Modal>
      {showCreateConfirmDialog && (
        <ConfirmDialog
          onConfirm={() =>
            isEditMode ? updateTransaction() : registerTransaction()
          }
          title="Registrar transacción recurrente"
          description={
            isEditMode
              ? "¿Deseas actualizar esta transacción recurrente?"
              : "¿Deseas registrar la nueva transacción recurrente?"
          }
          onClose={() => setShowCreateConfirmDialog(false)}
          isLoading={isRegistering}
        />
      )}
    </>
  );
};

export default FrequentTransactionModal;
